import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Clock,
  Calendar,
  User,
  ChevronRight,
  ArrowLeft,
  Tag,
  Search,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
} from 'lucide-react';
import { Button, Card, Input } from '@/components/ui';
import { H1, H2, H3, H4, Body, BodySm, BodyLg, Text } from '@/components/ui/Typography';
import { DisclaimerBanner, InlineDisclaimer } from '@/components/ui/DisclaimerBanner';
import { blogPosts, getBlogPost, getAllBlogPostSlugs, BlogPost } from '@/lib/blog-content';
import { cn } from '@/lib/utils';

// CTA Box Component for different color themes
interface CTABoxProps {
  config: BlogPost['ctaConfig'];
  colorTheme: BlogPost['colorTheme'];
}

function CTABox({ config, colorTheme }: CTABoxProps) {
  const [query, setQuery] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      window.location.href = `/chat?topic=${encodeURIComponent(query)}`;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        'rounded-xl p-6 my-8',
        `bg-gradient-to-r ${colorTheme.gradientFrom} ${colorTheme.gradientTo}`,
        `border ${colorTheme.borderColor}`
      )}
    >
      <div className="flex flex-col md:flex-row items-start gap-4">
        <div className={cn('p-3 rounded-full shrink-0', colorTheme.iconBg)}>
          <div className={colorTheme.iconColor}>
            {config.icon}
          </div>
        </div>
        <div className="flex-1 w-full">
          <h4 className={cn('font-bold text-lg', colorTheme.textPrimary)}>
            {config.headline}
          </h4>
          <p className={cn('mt-2', colorTheme.textSecondary)}>
            {config.subtext}
          </p>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={config.placeholder}
                className={cn(
                  'w-full px-4 py-3 pl-12 rounded-lg border-2',
                  `focus:outline-none focus:border-${colorTheme.buttonVariant === 'primary' ? 'primary' : colorTheme.buttonVariant === 'danger' ? 'red' : 'surface'}-500`,
                  'transition-all duration-200',
                  `border-${colorTheme.buttonVariant === 'primary' ? 'amber' : colorTheme.buttonVariant === 'danger' ? 'red' : 'surface'}-300`
                )}
              />
            </div>
            <Button
              variant={colorTheme.buttonVariant}
              size="lg"
              type="submit"
            >
              {config.buttonText}
            </Button>
          </form>
          <p className={cn('text-xs mt-2', colorTheme.textSecondary, 'opacity-75')}>
            {config.example}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// Related Posts Card Component
interface RelatedPostCardProps {
  post: BlogPost;
  onClick?: () => void;
}

function RelatedPostCard({ post, onClick }: RelatedPostCardProps) {
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <Link to={`/blog/${post.slug}`} onClick={onClick}>
        <Card
          variant="elevated"
          className="h-full hover:shadow-medium transition-shadow cursor-pointer group"
          padding="md"
        >
          <div className={cn(
            'text-xs font-medium uppercase tracking-wider mb-2',
            'bg-gradient-to-r bg-clip-text text-transparent',
            post.colorTheme.gradientFrom.replace('from-', 'from-').replace('-50', '-600'),
            post.colorTheme.gradientTo.replace('to-', 'to-').replace('-50', '-700')
          )}>
            {post.category}
          </div>
          <H4 className="group-hover:text-primary-600 transition-colors mb-2">
            {post.title}
          </H4>
          <BodySm color="muted" className="line-clamp-2 mb-4">
            {post.description}
          </BodySm>
          <div className="flex items-center text-primary-600 font-medium text-sm">
            Read more
            <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

// Blog Post Page Component
export function BlogPostPage() {
  const params = useParams();
  const slug = params.slug || '';
  const post = getBlogPost(slug);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!post) {
    return (
      <div className="min-h-screen bg-surface-50">
        <DisclaimerBanner />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <H1>Blog Post Not Found</H1>
          <Body className="mt-4 mb-8">
            The article you're looking for doesn't exist or has been moved.
          </Body>
          <Link to="/blog">
            <Button variant="primary">View All Articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = post.relatedPosts
    .map(slug => getBlogPost(slug))
    .filter((p): p is BlogPost => p !== undefined);

  // JSON-LD Article Schema
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    author: {
      '@type': 'Organization',
      name: post.author,
    },
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: 'NotLegal',
      logo: {
        '@type': 'ImageObject',
        url: 'https://notlegal.chat/logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://notlegal.chat/blog/${post.slug}`,
    },
    articleSection: post.category,
    keywords: post.keywords.join(', '),
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://notlegal.chat/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://notlegal.chat/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: `https://notlegal.chat/blog/${post.slug}`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-surface-50">
      <Helmet>
        <title>{post.title} | NotLegal</title>
        <meta name="description" content={post.description} />
        <meta name="keywords" content={post.keywords.join(', ')} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://notlegal.chat/blog/${post.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <DisclaimerBanner />

      {/* Navigation */}
      <nav className="bg-white border-b border-surface-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-surface-600 hover:text-primary-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Blog
          </Link>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <nav aria-label="Breadcrumb" className="flex items-center text-sm">
            <Link to="/" className="text-surface-500 hover:text-surface-700">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 text-surface-400" />
            <Link to="/blog" className="text-surface-500 hover:text-surface-700">
              Blog
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 text-surface-400" />
            <span className="text-surface-900 font-medium truncate max-w-[200px]">
              {post.title}
            </span>
          </nav>
        </motion.div>

        {/* Article Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 mb-4">
            <span className={cn(
              'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
              `bg-${post.colorTheme.iconBg.replace('bg-', '')} ${post.colorTheme.iconColor}`
            )}>
              <Tag className="w-3 h-3 mr-1" />
              {post.category}
            </span>
            <InlineDisclaimer />
          </div>

          <H1 className="mb-4 text-3xl md:text-4xl lg:text-5xl">
            {post.title}
          </H1>

          <Body color="muted" className="mb-6">
            {post.description}
          </Body>

          <div className="flex flex-wrap items-center gap-4 text-sm text-surface-500">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{new Date(post.publishedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>
          </div>
        </motion.header>

        {/* CTA Box - After Header */}
        <CTABox config={post.ctaConfig} colorTheme={post.colorTheme} />

        {/* Article Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="prose prose-surface max-w-none"
        >
          {post.content.sections.map((section, index) => (
            <section key={index} className="mb-8">
              <H2 className="text-2xl md:text-3xl mb-4 text-surface-900">
                {section.title}
              </H2>
              <Body className="text-surface-700 leading-relaxed">
                {section.content}
              </Body>
            </section>
          ))}
        </motion.div>

        {/* Final CTA Box */}
        <CTABox config={post.ctaConfig} colorTheme={post.colorTheme} />

        {/* Disclaimer Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card
            variant="outlined"
            padding="md"
            className="mt-8 bg-warning-50 border-warning-200"
          >
            <div className="flex items-start gap-3">
              <div className="bg-warning-100 p-2 rounded-full shrink-0">
                <AlertTriangle className="w-5 h-5 text-warning-600" />
              </div>
              <div>
                <H4 className="text-warning-900 text-base mb-1">
                  Important Disclaimer
                </H4>
                <BodySm className="text-warning-800">
                  This article provides general legal information and is not intended as legal advice for your specific situation.
                  Laws vary by state and jurisdiction, and the facts of your case may affect your legal rights and options.
                  Please consult with a licensed attorney in your state for advice regarding your particular circumstances.
                  This website is not a law firm and does not create an attorney-client relationship.
                </BodySm>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-16"
          >
            <H2 className="text-2xl md:text-3xl mb-6">
              Related Articles
            </H2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {relatedPosts.slice(0, 2).map((relatedPost) => (
                <RelatedPostCard key={relatedPost.slug} post={relatedPost} />
              ))}
            </div>
          </motion.section>
        )}
      </article>

      {/* Bottom Navigation */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          View All Articles
        </Link>
      </div>
    </div>
  );
}

// Blog Listing Page Component
export function BlogListingPage() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const posts = getAllBlogPostSlugs()
    .map(slug => getBlogPost(slug))
    .filter((p): p is BlogPost => p !== undefined);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [...new Set(posts.map(p => p.category))];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-surface-50">
      <Helmet>
        <title>Legal Guides & Resources | NotLegal Blog</title>
        <meta
          name="description"
          content="Expert legal guides on tenant rights, employment law, small claims court, divorce, and traffic tickets. Free resources to help you understand your legal options."
        />
        <meta name="keywords" content="legal guides, tenant rights, employment law, divorce, traffic tickets, small claims" />
        <meta property="og:title" content="Legal Guides & Resources | NotLegal Blog" />
        <meta property="og:description" content="Expert legal guides on tenant rights, employment law, small claims court, divorce, and traffic tickets." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://notlegal.chat/blog" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Legal Guides Blog',
            description: 'Expert legal guides and resources',
            mainEntity: {
              '@type': 'ItemList',
              numberOfItems: posts.length,
              itemListElement: posts.map((post, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                item: {
                  '@type': 'Article',
                  headline: post.title,
                  url: `https://notlegal.chat/blog/${post.slug}`,
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <DisclaimerBanner />

      {/* Hero Section */}
      <section className="bg-white border-b border-surface-100">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <H1 className="mb-4">
              Legal Guides & Resources
            </H1>
            <BodyLg color="muted" className="max-w-2xl mx-auto">
              Expert guides on tenant rights, employment law, small claims court, divorce, and traffic violations. Understand your legal options with confidence.
            </BodyLg>
          </motion.div>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-md mx-auto mt-8"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="w-full px-4 py-3 pl-12 pr-4 rounded-xl border-2 border-surface-200 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
              />
            </div>
          </motion.div>

          {/* Category Filters */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-2 mt-6"
          >
            <button
              onClick={() => setSelectedCategory(null)}
              className={cn(
                'px-4 py-2 rounded-full text-sm font-medium transition-all',
                !selectedCategory
                  ? 'bg-primary-600 text-white'
                  : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
              )}
            >
              All
            </button>
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                )}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Link to={`/blog/${post.slug}`}>
                <Card
                  variant="elevated"
                  className="h-full hover:shadow-medium transition-all group cursor-pointer"
                  padding="lg"
                >
                  {/* Category Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={cn(
                      'text-xs font-medium uppercase tracking-wider px-2 py-1 rounded-full',
                      post.colorTheme.iconBg,
                      post.colorTheme.iconColor
                    )}>
                      {post.category}
                    </span>
                    <BodySm color="muted" className="flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {post.readTime}
                    </BodySm>
                  </div>

                  {/* Title */}
                  <H3 className="text-xl mb-3 group-hover:text-primary-600 transition-colors">
                    {post.title}
                  </H3>

                  {/* Description */}
                  <Body color="muted" className="mb-4 line-clamp-3">
                    {post.description}
                  </Body>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-surface-100">
                    <BodySm color="muted">
                      {new Date(post.publishedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </BodySm>
                    <span className="inline-flex items-center text-primary-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                      Read article
                      <ChevronRightIcon className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <Body color="muted">
              No articles found matching your search.
            </Body>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <Card
          variant="elevated"
          padding="lg"
          className="bg-primary-50 border-primary-200 text-center"
        >
          <H3 className="mb-2">Have a Legal Question?</H3>
          <Body color="muted" className="mb-6 max-w-lg mx-auto">
            Get personalized guidance about your specific situation. Our AI assistant can help you understand your options.
          </Body>
          <Link to="/chat">
            <Button variant="primary" size="lg">
              Ask a Question
            </Button>
          </Link>
        </Card>
      </section>
    </div>
  );
}
